import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ChevronLeft, Plus } from "lucide-react";
import { setPageMeta, injectJsonLd, removeJsonLd } from "@/lib/seo";
import { productCategorySchema } from "@/lib/schema";

const KINDS = [
  { value: "", label: "Tutti" },
  { value: "libro", label: "📖 Libri" },
  { value: "gioco", label: "🧩 Giochi" },
  { value: "app", label: "📱 App" },
];

const AGE_BUCKETS = [
  { value: null, label: "Tutte le età" },
  { value: 2, label: "0-3 anni" },
  { value: 5, label: "3-6 anni" },
  { value: 7, label: "6-8 anni" },
];

export default function ProductCategory() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [age, setAge] = useState(null);

  useEffect(() => {
    api.get("/products/categories")
      .then(({ data }) => setCategory(data.find((c) => c.slug === slug) || null))
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    setLoading(true);
    const params = { category: slug, limit: 100 };
    if (kind) params.kind = kind;
    if (age !== null) params.age = age;
    api.get("/products", { params })
      .then(({ data }) => setProducts(data.items || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [slug, kind, age]);

  // SEO meta + JSON-LD (invisibili all'utente)
  useEffect(() => {
    if (!category) return;
    const title = `${category.label} — libri, giochi e app educative · Per manina`;
    const description = `Selezione curata di libri, giochi e app educative sul tema "${category.label}" per bambini 0-8 anni. Recensiti dai genitori italiani della community.`.slice(0, 250);
    setPageMeta({
      title,
      description,
      canonicalPath: `/prodotti/${category.slug}`,
      ogType: "website",
    });
    injectJsonLd("schema-category", productCategorySchema(category, products));
    return () => removeJsonLd("schema-category");
  }, [category, products]);

  const counts = useMemo(() => {
    const by = { libro: 0, gioco: 0, app: 0 };
    products.forEach((p) => { by[p.kind] = (by[p.kind] || 0) + 1; });
    return by;
  }, [products]);

  if (!category) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 font-ui text-[#5C6B64]">
        Categoria in caricamento…{" "}
        <Link to="/prodotti" className="underline text-[#C05C45]">torna alle aree</Link>
      </div>
    );
  }

  return (
    <div className="bg-[#FAF9F6]">
      {/* HEADER */}
      <section
        className="border-b border-[#E8E6E1]"
        style={{ background: `linear-gradient(135deg, ${category.hex}15, transparent 70%)` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link
            to="/prodotti"
            data-testid="back-to-hub-link"
            className="inline-flex items-center gap-1 font-ui text-sm text-[#5C6B64] hover:text-[#C05C45]"
          >
            <ChevronLeft size={16} /> Tutte le aree
          </Link>
          <div className="mt-4 flex items-start gap-5">
            <div className="text-6xl" aria-hidden>{category.emoji}</div>
            <div className="flex-1">
              <p className="label-eyebrow text-[#C05C45]">Area tematica</p>
              <h1 className="mt-1 font-display text-3xl sm:text-4xl text-[#1A362D] tracking-tight">{category.label}</h1>
              <p className="mt-2 font-ui text-sm text-[#5C6B64] max-w-2xl">{category.tagline}</p>
              <Link
                to={`/consiglia-materiale?category=${category.slug}`}
                data-testid="suggest-product-from-category"
                className="mt-4 btn-secondary text-xs inline-flex items-center gap-1.5"
              >
                <Plus size={13} /> Consiglia materiale per quest'area
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
        {/* FILTERS */}
        <aside className="space-y-8 order-2 lg:order-1" data-testid="product-filters">
          <div>
            <p className="font-ui text-xs uppercase tracking-wider text-[#8A9691] mb-3">Tipo</p>
            <div className="space-y-1">
              {KINDS.map((k) => (
                <button
                  key={k.value}
                  onClick={() => setKind(k.value)}
                  data-testid={`filter-kind-${k.value || "all"}`}
                  className={`block w-full text-left font-ui text-sm px-3 py-2 rounded-md transition-colors ${
                    kind === k.value
                      ? "bg-[#1A362D] text-white"
                      : "text-[#1A362D] hover:bg-[#F2EFEB]"
                  }`}
                >
                  {k.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="font-ui text-xs uppercase tracking-wider text-[#8A9691] mb-3">Fascia età</p>
            <div className="space-y-1">
              {AGE_BUCKETS.map((a) => (
                <button
                  key={String(a.value)}
                  onClick={() => setAge(a.value)}
                  data-testid={`filter-age-${a.value ?? "all"}`}
                  className={`block w-full text-left font-ui text-sm px-3 py-2 rounded-md transition-colors ${
                    age === a.value
                      ? "bg-[#1A362D] text-white"
                      : "text-[#1A362D] hover:bg-[#F2EFEB]"
                  }`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          <div className="text-xs text-[#8A9691] font-ui pt-4 border-t border-[#E8E6E1]">
            📖 {counts.libro || 0} libri · 🧩 {counts.gioco || 0} giochi · 📱 {counts.app || 0} app
          </div>
        </aside>

        {/* GRID */}
        <div className="order-1 lg:order-2">
          {loading ? (
            <p className="font-ui text-[#8A9691]">Caricamento prodotti…</p>
          ) : products.length === 0 ? (
            <div className="bg-white border border-[#E8E6E1] rounded-xl p-10 text-center">
              <p className="font-ui text-[#5C6B64]">Nessun prodotto trovato con questi filtri.</p>
              <Link to={`/consiglia-materiale?category=${slug}`} className="inline-block mt-4 btn-primary text-sm">
                Consiglia un prodotto per quest'area
              </Link>
            </div>
          ) : (
            <>
              <p className="font-ui text-sm text-[#5C6B64] mb-5">
                <span data-testid="products-count" className="font-semibold text-[#1A362D]">{products.length}</span> {products.length === 1 ? "prodotto" : "prodotti"}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {products.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
